import React, { Component } from 'react';
import { Formik, yupToFormErrors } from 'formik';
import * as yup from 'yup';
import styled from 'styled-components';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';

import Form from './Form';
import FieldSet from './FieldSet';
import Input from './Input';
import RadioButtonGroup from './RadioButtonGroup';
import RadioButton from './RadioButton';
import Textarea from './Textarea';
import PrivacyPolicyCheckbox from './PrivacyPolicyCheckbox';
import { TextButton, LoadingButton } from '../Buttons';

const SuccessMessage = styled.p`
  text-align: center;
  font-weight: ${p => p.theme.weights.bold};
`;

const StyledComplaintsForm = styled.div`
  margin: 0 auto;
  max-width: ${p => p.theme.globalWidthSmall};
`;

const Row = styled.div`
  display: flex;
  margin: 0 -${p => p.theme.gutterXSmall};
`;

const Column = styled.div`
  flex: 1;
  margin: 0 ${p => p.theme.gutterXSmall} 30px ${p => p.theme.gutterXSmall};
`;

const Page = styled.div`
  font-size: 1rem;
  font-style: italic;
  margin-bottom: 20px;
`;

const BackButton = styled(TextButton)`
  display: block;
`;

const FormSectionHeader = ({ title }) => <div>
  <h3>
    {title}
  </h3>
  <hr
    style={{
      marginBottom: '24px'
    }}
  />
</div>

const validation = step => {
  const stepsValidations = {
    1: {
      insuranceCompany: yup.string().required(),
      insuranceCompanyContact: yup.string().required(),
      insuranceCompanyEmail: yup.string().email().required(),
      insuranceCompanyPhone: yup.string().required(),
      insuranceCompanyMessage: yup.string()
    },
    2: {
      carDeliveryTo: yup.string().required(),
      carRegNumber: yup.string().required(),
      carType: yup.string().required(),
      carState: yup.string().required(),
      carValue: yup.string().required(),
      carWheels: yup.number().required(),
      carKeys: yup.number().required(),
      carOwnerPhone: yup.string().required()
    },
    3: {
      customerType: yup.string().required(),
      name: yup.string().required(),
      address: yup.string(),
      place: yup.string(),
      phone: yup.string().required(),
      accepted: yup
        .bool()
        .oneOf([true], 'Du må godkjenne personvernerklæringen.')
    },
  };

  return stepsValidations[step];
};

const mapValuesToFields = values => {
  const fields = [
    {
      key: 'input_25',
      value: values.customerType,
    },
    {
      key: 'input_10',
      value: values.insuranceCompany,
    },
    {
      key: 'input_11',
      value: values.insuranceCompanyContact,
    },
    {
      key: 'input_6',
      value: values.insuranceCompanyEmail,
    },
    {
      key: 'input_3',
      value: values.insuranceCompanyPhone,
    },
    {
      key: 'input_5',
      value: values.insuranceCompanyMessage,
    },
    {
      key: 'input_13',
      value: values.carDeliveryTo,
    },
    {
      key: 'input_8',
      value: values.carRegNumber,
    },
    {
      key: 'input_14',
      value: values.carType,
    },
    {
      key: 'input_15',
      value: values.carState,
    },
    {
      key: 'input_16',
      value: values.carValue,
    },
    {
      key: 'input_17',
      value: values.carWheels,
    },
    {
      key: 'input_18',
      value: values.carKeys,
    },
    {
      key: 'input_19',
      value: values.carOwnerPhone,
    },
    {
      key: 'input_21',
      value: values.name,
    },
    {
      key: 'input_22',
      value: values.address,
    },
    {
      key: 'input_23',
      value: values.place,
    },
    {
      key: 'input_24',
      value: values.phone,
    }
  ];

  return fields;
};

yup.setLocale({
  mixed: {
    required: 'Dette feltet er obligatorisk.'
  },
  string: {
    email: 'Du må skrive inn en gyldig e-postadresse.',
    url: 'Du må skrive inn en gyldig nettadresse.',
    max: 'Du kan maks skrive inn ${max} tegn.' // eslint-disable-line no-template-curly-in-string
  }
});

export default class ComplaintsForm extends Component {
  state = {
    step: 1,
    totalSteps: 3,
    success: false
  };

  render() {
    const { step, totalSteps, success } = this.state;
    const { formId } = this.props;

    if (success) {
      return (
        <StyledComplaintsForm>
          <SuccessMessage>
            Takk for din henvendelse! Vi vil ta kontakt om kort tid.
                    </SuccessMessage>
        </StyledComplaintsForm>
      );
    }

    return (
      <StyledComplaintsForm>
        <Mutation
          mutation={gql`
            mutation submitForm($submission: GFFormSubmission $id: Int) {
              submission:postGFFormSubmission(
                id: $id
                submission: $submission
              ){
                is_valid
                page_number
                source_page_number
                confirmation_message
              }
            }
        `}
        >
          {(submit, { loading, error, data }) => <Formik
            initialValues={{
              name: '',
              address: '',
              orgNumber: '',
              email: '',
              phone: '',
              invoiceNumber: '',
              cause: '',
              reason: '',
              where: '',
              measures: '',
              accepted: false
            }}
            validationSchema={yup.object().shape(validation(step))}
            onSubmit={(values, { setSubmitting }) => {
              console.log('Submitting step:', step);
              if (step < 3) {
                this.setState({ step: step + 1 });

                setSubmitting(false);

                return;
              }

              console.log(values);

              submit({
                variables: {
                  id: formId,
                  submission: {
                    fields: mapValuesToFields(values)
                  }
                }
              }).catch((error) => {
                console.error(error);

                setSubmitting(false);
              }).then((data) => {
                setSubmitting(false);
              });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting
            }) => <div>
                {(data && data.submission) && <div
                  dangerouslySetInnerHTML={{
                    __html: data.submission.confirmation_message
                  }}
                ></div>}
                {!(data && data.submission) && <Form onSubmit={handleSubmit}>
                  <Page>
                    Trinn {step} av {totalSteps}
                  </Page>
                  {error && <div>
                    {error.toString()}
                  </div>}

                  {step === 1 && <FieldSet>
                    <FormSectionHeader
                      title="Om forsikringsselskapet"
                    />
                    <Row>
                      <Column>
                        <Input
                          name="insuranceCompany"
                          label="Forsikringsselskap"
                          isRequired={true}
                          value={values.insuranceCompany}
                          error={errors.insuranceCompany}
                          touched={touched.insuranceCompany}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Column>
                      <Column>
                        <Input
                          name="insuranceCompanyContact"
                          label="Saksbehandler"
                          placeholder="Fornavn Etternavn"
                          isRequired={true}
                          value={values.insuranceCompanyContact}
                          error={errors.insuranceCompanyContact}
                          touched={touched.insuranceCompanyContact}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Column>
                    </Row>

                    <Row>
                      <Column>
                        <Input
                          name="insuranceCompanyEmail"
                          label="E-post"
                          isRequired={true}
                          value={values.insuranceCompanyEmail}
                          error={errors.insuranceCompanyEmail}
                          touched={touched.insuranceCompanyEmail}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Column>
                      <Column>
                        <Input
                          name="insuranceCompanyPhone"
                          label="Telefon"
                          isRequired={true}
                          value={values.insuranceCompanyPhone}
                          error={errors.insuranceCompanyPhone}
                          touched={touched.insuranceCompanyPhone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Column>
                    </Row>

                    <Row>
                      <Column>
                        <Textarea
                          name="insuranceCompanyMessage"
                          label="Melding"
                          value={values.insuranceCompanyMessage}
                          error={errors.insuranceCompanyMessage}
                          touched={touched.insuranceCompanyMessage}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Column>
                    </Row>

                    <LoadingButton type="submit" loading={isSubmitting}>
                      Neste »
                    </LoadingButton>
                  </FieldSet>}

                  {step === 2 && <FieldSet>
                    <FormSectionHeader
                      title="Om skadebilen"
                    />

                    <Row>
                      <Column>
                        <RadioButtonGroup
                          legend="Skadebilen leveres til"
                          isRequired={true}
                          error={errors.carDeliveryTo}
                          touched={touched.carDeliveryTo}
                        >
                          <RadioButton
                            name="carDeliveryTo"
                            label="Demontering"
                            value="Demontering"
                            checked={values.carDeliveryTo === 'Demontering'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />

                          <RadioButton
                            name="carDeliveryTo"
                            label="Oppbevaring"
                            value="Oppbevaring"
                            checked={values.carDeliveryTo === 'Oppbevaring'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </RadioButtonGroup>
                      </Column>
                    </Row>

                    <Row>
                      <Column>
                        <Input
                          name="carRegNumber"
                          label="Reg.nr"
                          isRequired={true}
                          value={values.carRegNumber}
                          error={errors.carRegNumber}
                          touched={touched.carRegNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Column>
                    </Row>

                    <Row>
                      <Column>
                        <Input
                          name="carType"
                          label="Biltype"
                          isRequired={true}
                          value={values.carType}
                          error={errors.carType}
                          touched={touched.carType}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Column>
                    </Row>

                    <Row>
                      <Column>
                        <RadioButtonGroup
                          legend="Tilstand"
                          isRequired={true}
                          error={errors.carState}
                          touched={touched.carState}
                        >
                          <RadioButton
                            name="carState"
                            label="Kjørbar"
                            value="Kjørbar"
                            checked={values.carState === 'Kjørbar'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <RadioButton
                            name="carState"
                            label="Trillbar"
                            value="Trillbar"
                            checked={values.carState === 'Trillbar'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <RadioButton
                            name="carState"
                            label="Brann"
                            value="Brann"
                            checked={values.carState === 'Brann'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <RadioButton
                            name="carState"
                            label="Tyveri"
                            value="Tyveri"
                            checked={values.carState === 'Tyveri'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </RadioButtonGroup>
                      </Column>
                    </Row>

                    <Row>
                      <Column>
                        <Input
                          name="carValue"
                          label="Førverdi (ekskl. reg.avgift)"
                          isRequired={true}
                          value={values.carValue}
                          error={errors.carValue}
                          touched={touched.carValue}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Column>
                    </Row>

                    <Row>
                      <Column>
                        <RadioButtonGroup
                          legend="Antall hjulsett som følger med"
                          isRequired={true}
                          error={errors.carWheels}
                          touched={touched.carWheels}
                        >
                          <RadioButton
                            name="carWheels"
                            label="1"
                            value="1"
                            checked={values.carWheels === '1'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />

                          <RadioButton
                            name="carWheels"
                            label="2"
                            value="2"
                            checked={values.carWheels === '2'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </RadioButtonGroup>
                      </Column>
                    </Row>

                    <Row>
                      <Column>
                        <RadioButtonGroup
                          legend="Antall nøkler som følger med"
                          isRequired={true}
                          error={errors.carKeys}
                          touched={touched.carKeys}
                        >
                          <RadioButton
                            name="carKeys"
                            label="0"
                            value="0"
                            checked={values.carKeys === '0'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <RadioButton
                            name="carKeys"
                            label="1"
                            value="1"
                            checked={values.carKeys === '1'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <RadioButton
                            name="carKeys"
                            label="2"
                            value="2"
                            checked={values.carKeys === '2'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </RadioButtonGroup>
                      </Column>
                    </Row>

                    <Row>
                      <Column>
                        <Input
                          name="carOwnerPhone"
                          label="Telefonnr. til bileier"
                          isRequired={true}
                          value={values.carOwnerPhone}
                          error={errors.carOwnerPhone}
                          touched={touched.carOwnerPhone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Column>
                    </Row>

                    <LoadingButton type="submit" loading={isSubmitting}>
                      Neste »
                    </LoadingButton>
                    <BackButton
                      type="button"
                      onClick={() => this.setState({ step: step - 1 })}
                    >
                      Gå tilbake
                    </BackButton>
                  </FieldSet>}

                  {step === 3 && <FieldSet>
                    <FormSectionHeader
                      title="Kontakt og hentested"
                    />
                    <Row>
                      <Column>
                        <RadioButtonGroup
                          legend="Kunde"
                          value={values.customerType}
                          error={errors.customerType}
                          touched={touched.customerType}
                        >
                          <RadioButton
                            name="customerType"
                            label="Bedriftskunde"
                            value="Bedriftskunde"
                            checked={values.customerType === 'Bedriftskunde'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />

                          <RadioButton
                            name="customerType"
                            label="Privatkunde"
                            value="Privatkunde"
                            checked={values.customerType === 'Privatkunde'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </RadioButtonGroup>
                      </Column>
                    </Row>

                    <Row>
                      <Column>
                        <Input
                          name="name"
                          label="Navn"
                          isRequired={true}
                          value={values.name}
                          error={errors.name}
                          touched={touched.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Column>

                      <Column>
                        <Input
                          name="address"
                          label="Adresse"
                          isRequired={true}
                          value={values.address}
                          error={errors.address}
                          touched={touched.address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Column>
                    </Row>

                    <Row>
                      <Column>
                        <Input
                          name="place"
                          label="Postnr og sted"
                          isRequired={true}
                          value={values.place}
                          error={errors.place}
                          touched={touched.place}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Column>

                      <Column>
                        <Input
                          name="phone"
                          label="Telefon"
                          isRequired={true}
                          value={values.phone}
                          error={errors.phone}
                          touched={touched.phone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Column>
                    </Row>

                    <Row>
                      <Column>
                        <PrivacyPolicyCheckbox
                          name="accepted"
                          value={values.accepted}
                          checked={values.accepted}
                          error={errors.accepted}
                          touched={touched.accepted}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Column>
                    </Row>

                    <LoadingButton type="submit" loading={isSubmitting}>
                      Send skjema
                    </LoadingButton>
                    <BackButton
                      type="button"
                      onClick={() => this.setState({ step: step - 1 })}
                    >
                      Gå tilbake
                    </BackButton>
                  </FieldSet>}
                </Form>}
              </div>}
          </Formik>}
        </Mutation>
      </StyledComplaintsForm>
    );
  }
}

